import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookMarked, FileSpreadsheet, Loader2 } from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AudienceImportSession } from '@/types/components/audiences/import-session'

type SheetOption = NonNullable<
  AudienceImportSession['sheetAssignment']
>['options'][number]

export interface WorkbookAssignmentCardProps {
  options: SheetOption[]
  audienceSheet: string | null
  dictionarySheet: string | null
  isSaving: boolean
  onSubmit: (payload: {
    audienceSheet: string
    dictionarySheet?: string | null
  }) => Promise<void>
  /** Reports the current selection so the parent can own the CTA. */
  onChange?: (payload: {
    audienceSheet: string
    dictionarySheet: string | null
    canSubmit: boolean
  }) => void
  /** When true, the card does not render its own Continue button. */
  externalCta?: boolean
}

export function WorkbookAssignmentCard({
  options,
  audienceSheet,
  dictionarySheet,
  isSaving,
  onSubmit,
  onChange,
  externalCta,
}: WorkbookAssignmentCardProps) {
  const { t } = useTranslation('app')
  const defaultAudience = audienceSheet ?? options[0]?.sheetName ?? ''
  const defaultDictionary =
    dictionarySheet ?? (options.length > 1 ? options[1].sheetName : '__none__')

  const [nextAudienceSheet, setNextAudienceSheet] = useState(defaultAudience)
  const [nextDictionarySheet, setNextDictionarySheet] = useState(defaultDictionary)

  useEffect(() => {
    if (audienceSheet != null) setNextAudienceSheet(audienceSheet)
  }, [audienceSheet])

  useEffect(() => {
    if (dictionarySheet != null) setNextDictionarySheet(dictionarySheet)
  }, [dictionarySheet])

  useEffect(() => {
    onChange?.({
      audienceSheet: nextAudienceSheet,
      dictionarySheet:
        nextDictionarySheet === '__none__' ? null : nextDictionarySheet,
      canSubmit: nextAudienceSheet.trim().length > 0,
    })
  }, [nextAudienceSheet, nextDictionarySheet, onChange])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 rounded-2xl bg-card p-7">
        {/* Data sheet row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="flex flex-col">
              <p className="text-base font-medium leading-[1.6] text-foreground dark:text-white">
                {t('audienceCreate.dataSheet')}
              </p>
              <p className="text-sm leading-[1.5] text-muted-foreground">
                {t('audienceCreate.dataSheetHint')}
              </p>
            </div>
          </div>
          <Select
            value={nextAudienceSheet || undefined}
            onValueChange={(value) => {
              const prev = nextAudienceSheet
              setNextAudienceSheet(value)
              if (nextDictionarySheet === value) {
                setNextDictionarySheet(prev || '__none__')
              }
            }}
          >
            <SelectTrigger className="h-[38px] w-[280px] shrink-0 rounded-md border-border bg-transparent text-sm text-foreground dark:text-white">
              <SelectValue placeholder={t('audienceCreate.chooseSheet')} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.sheetName} value={option.sheetName}>
                  {option.sheetName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Separator */}
        <div className="h-px w-full bg-border" />

        {/* Dictionary sheet row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <BookMarked className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="flex flex-col">
              <p className="text-base font-medium leading-[1.6] text-foreground dark:text-white">
                {t('audienceCreate.dictionarySheet')}
              </p>
              <p className="text-sm leading-[1.5] text-muted-foreground">
                {t('audienceCreate.dictionaryHint')}
              </p>
            </div>
          </div>
          <Select
            value={nextDictionarySheet}
            onValueChange={(value) => {
              const prev = nextDictionarySheet
              setNextDictionarySheet(value)
              if (value !== '__none__' && value === nextAudienceSheet) {
                setNextAudienceSheet(prev !== '__none__' ? prev : '')
              }
            }}
          >
            <SelectTrigger className="h-[38px] w-[280px] shrink-0 rounded-md border-border bg-transparent text-sm text-foreground dark:text-white">
              <SelectValue placeholder={t('audienceCreate.none')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t('audienceCreate.none')}</SelectItem>
              {options.map((option) => (
                <SelectItem key={option.sheetName} value={option.sheetName}>
                  {option.sheetName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!externalCta ? (
        <div className="flex justify-center">
          <Button
            type="button"
            shape="pill"
            size="lg"
            disabled={isSaving || nextAudienceSheet.trim().length === 0}
            className={AUDIENCE_WIZARD_BUTTON_CLASS}
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            onClick={() =>
              void onSubmit({
                audienceSheet: nextAudienceSheet,
                dictionarySheet:
                  nextDictionarySheet === '__none__' ? null : nextDictionarySheet,
              })
            }
          >
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t('audienceCreate.continue')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
